import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import {PrismicRichText} from '@prismicio/react'
import {useRouter} from 'next/router'
import {resolveParentBasePath} from 'prismicio'
import type {FC} from 'react'
import {useCallback, useMemo, useRef, useState} from 'react'
import {DEFAULT_LOCALE} from 'src/common/constants'
import {Container} from 'src/common/Container'
import {CATEGORY_MAP} from 'src/common/hooks/useGetBlogPostCategory'
import type {Context} from 'src/common/types'
import {normalizeCategoryValue} from 'src/common/utils/categoryTransform'
import {CategoryFilter} from './components/CategoryFilter'
import {PaginationControls} from './components/PaginationControls'
import {PaginationSEO} from './components/PaginationSEO'
import {PostsGrid} from './components/PostsGrid'

/**
 * Props for `BlogPostsContainer`.
 */
export type BlogPostsContainerProps = SliceComponentProps<Content.BlogPostsContainerSlice> & {
  context: Context
}

/**
 * Component for "BlogPostsContainer" Slices.
 */
const BlogPostsContainer: FC<BlogPostsContainerProps> = ({slice, context}) => {
  const router = useRouter()
  const locale = router.locale || DEFAULT_LOCALE
  const formattedLocale = locale === DEFAULT_LOCALE ? '' : `${locale}/`
  const containerRef = useRef<HTMLDivElement>(null)

  // Get blog posts and categories from context
  const allBlogPosts = useMemo(() => context.blogPosts || [], [context.blogPosts])
  const baseUrl = `/${formattedLocale}${context.pageUid}`

  // Add "All" category at the beginning
  const categories = useMemo(() => {
    const allCategory = {category_name: 'All', value: 'all'}
    const allCategories = context.categories || []

    return [allCategory, ...allCategories]
  }, [context.categories])

  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0)
  const [currentClientPage, setCurrentClientPage] = useState(1)

  const filteredBlogPosts = useMemo(() => {
    if (activeCategoryIndex === 0) {
      return allBlogPosts
    }

    const selectedCategory = categories[activeCategoryIndex]?.value

    return allBlogPosts.filter((post) => {
      if (!post.data.category) {
        return false
      }

      const localizedName =
        CATEGORY_MAP[locale as keyof typeof CATEGORY_MAP]?.[post.data.category] || post.data.category

      return normalizeCategoryValue(localizedName) === selectedCategory
    })
  }, [activeCategoryIndex, allBlogPosts, categories, locale])

  const POSTS_PER_PAGE = 6
  const totalPages = Math.ceil(filteredBlogPosts.length / POSTS_PER_PAGE)

  const paginatedPosts = filteredBlogPosts.slice(
    (currentClientPage - 1) * POSTS_PER_PAGE,
    currentClientPage * POSTS_PER_PAGE,
  )

  const handleCategoryChange = useCallback((index: number) => {
    setActiveCategoryIndex(index)
    setCurrentClientPage(1)
  }, [])

  const scrollToContainer = () => {
    if (containerRef.current) {
      const top = containerRef.current.getBoundingClientRect().top + window.scrollY - 20

      window.scrollTo({top, behavior: 'smooth'})
    }
  }

  return (
    <>
      <PaginationSEO
        currentPage={currentClientPage}
        totalPages={totalPages}
        baseUrl={baseUrl}
        activeCategory={activeCategoryIndex > 0 ? categories[activeCategoryIndex]?.value : undefined}
      />

      <Container className="isolate !overflow-visible !py-12" backgroundColor="Grey/100">
        <div
          ref={containerRef}
          className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 px-5 md:px-10 lg:gap-12 lg:px-5 xl:px-0"
        >
          {/* Header section */}
          <div className="flex flex-col gap-6">
            <PrismicRichText
              field={slice.primary.title}
              components={{
                heading2: ({children}) => (
                  <h2 className="text-24 font-medium leading-[30px] text-moss-black">{children}</h2>
                ),
              }}
            />
            <CategoryFilter
              categories={categories}
              activeIndex={activeCategoryIndex}
              onCategoryChange={handleCategoryChange}
              onScroll={scrollToContainer}
            />
          </div>

          {/* Posts grid */}
          <PostsGrid
            posts={paginatedPosts}
            loading={false}
            currentPage={currentClientPage}
            pageUid={context.pageUid || 'blog'}
            buttonLabel={slice.primary.button_label}
          />

          {/* Hidden links for crawler discoverability */}
          <nav aria-hidden="true" className="hidden">
            {allBlogPosts.map((post) => {
              const parentPage = post.data.parent_page

              if (!isFilled.contentRelationship(parentPage) || !post.uid) {
                return null
              }

              const basePath = resolveParentBasePath(parentPage)

              if (!basePath) {
                return null
              }

              return (
                <a key={post.id} href={`${basePath}/${post.uid}`}>
                  {post.data.title}
                </a>
              )
            })}
          </nav>

          {/* Pagination controls */}
          <PaginationControls
            totalPages={totalPages}
            currentPage={currentClientPage}
            baseUrl={baseUrl}
            activeCategory={activeCategoryIndex > 0 ? categories[activeCategoryIndex]?.value : undefined}
            onScroll={scrollToContainer}
            onPageChange={setCurrentClientPage}
          />
        </div>
      </Container>
    </>
  )
}

export default BlogPostsContainer
